import type { AxiosInstance } from 'axios';

import Vue from 'vue';

import type { NotifyFunction } from '@/plugins/notifications';

import { autoRefillSettings } from '@/api/endpoints/billing';
import { companyBilling } from '@/api/endpoints/user-company';

interface AccountServerData {
  id: string
  dadd: string
  num: string
  tp: string
  empl_id: string
  balance: number
  hold: number
  limit: number
  balanceFree: number
  lstupdt: string
  userid: string
}

export interface BillingAccount {
  id: string
  balance: number
  num: string
}
export interface InvestmentsBillingData {
  inProjectsTotal: number
}
export interface AutowalletSettings {
  debtPercentage: number
  interestPercentage: number
}

const innerStore = defineStore('user-billing', () => {
  // возможно плохая идея т.к. нарушает dependency inversion
  const http = Vue.http as AxiosInstance;
  const notify = Vue.notify as NotifyFunction;

  const isLoading = ref<boolean>(false);

  const investorAccount = shallowRef<BillingAccount | null>(null);
  const autoAccount = shallowRef<BillingAccount | null>(null);
  const investorTotalBalance = ref<number>(0);

  const borrowerAccount = shallowRef<BillingAccount | null>(null);
  const borrowerTotalBalance = ref<number>(0);

  const investmentsData = shallowRef<InvestmentsBillingData | null>(null);
  const inProjectsTotal = ref<number>(0);

  const settings = shallowRef<AutowalletSettings | null>(null);

  async function update() {
    isLoading.value = true;
    await http
      .get(companyBilling)
      .then((response) => {
        if (!response.data)
          return Promise.reject(response);

        const res = response.data.response;

        investorAccount.value = res?.acc?.find((a: AccountServerData) => a?.tp?.toLowerCase() === 'inv') ?? null;
        autoAccount.value = res?.acc?.find((a: AccountServerData) => a?.tp?.toLowerCase() === 'auto') ?? null;
        investorTotalBalance.value = (investorAccount.value?.balance ?? 0) + (autoAccount.value?.balance ?? 0);

        borrowerAccount.value = res?.acc?.find((a: AccountServerData) => a?.tp?.toLowerCase() === 'dbt') ?? null;
        borrowerTotalBalance.value = borrowerAccount.value?.balance ?? 0;

        investmentsData.value = res?.investment
          ? { inProjectsTotal: res?.investment.sum_in_projects }
          : null;
        inProjectsTotal.value = investmentsData.value?.inProjectsTotal ?? 0;

        settings.value = res?.refill_auto_setting
          ? {
              debtPercentage: res.refill_auto_setting.debt_share,
              interestPercentage: res.refill_auto_setting.percent_share,
            }
          : null;
      })
      .finally(() => {
        isLoading.value = false;
      });
  }

  async function disableAutowallet() {
    return http.post(autoRefillSettings, { action: 'delete' }).then((response) => {
      settings.value = null;

      notify({
        text: 'Автопополнение отключено',
        type: 'success',
      });

      return response;
    });
  }

  async function setAutowallet(payload: AutowalletSettings) {
    return http.post(autoRefillSettings, {
      action: settings.value ? 'apply' : 'create',
      debt_share: payload.debtPercentage,
      percent_share: payload.interestPercentage,
    }).then((response) => {
      settings.value = payload;

      notify({
        text: 'Настройки автопополнения сохранены',
        type: 'success',
      });

      return response;
    });
  }

  function reset() {
    investorAccount.value = null;
    autoAccount.value = null;
    borrowerAccount.value = null;
    investmentsData.value = null;
    settings.value = null;

    borrowerTotalBalance.value = 0;
    investorTotalBalance.value = 0;
    inProjectsTotal.value = 0;
  }

  async function resetAfter(callback: CallableFunction) {
    isLoading.value = true;
    await callback();
    reset();
    isLoading.value = false;
  }

  return {
    isLoading,

    investorAccount,
    autoAccount,
    borrowerAccount,

    investmentsData,
    settings,

    investorTotalBalance,
    borrowerTotalBalance,
    inProjectsTotal,

    update,

    disableAutowallet,
    setAutowallet,

    reset,
    resetAfter,
  };
});

export interface UseUserBillingOptions {
  init: boolean
}

const defaultOptions = { init: false };

export function useUserBilling(options: UseUserBillingOptions = defaultOptions) {
  const { init } = options;

  const store = innerStore();

  if (init && !store.isLoading)
    store.update();

  return store;
}
